// src/utils/e2e.js — end-to-end encryption primitives (NaCl box / Curve25519).
// The secret key never leaves this device; only the public key is published.
// Used to encrypt student↔student DMs so the server only stores ciphertext.
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import api from './api';

const SK_KEY  = 'tt_e2e_sk';
const PK_KEY  = 'tt_e2e_pk';
const PUB_KEY = 'tt_e2e_published';

export function getKeyPair() {
  const skB64 = localStorage.getItem(SK_KEY);
  const pkB64 = localStorage.getItem(PK_KEY);
  if (skB64 && pkB64) {
    return { secretKey: util.decodeBase64(skB64), publicKeyB64: pkB64 };
  }
  const kp = nacl.box.keyPair();
  const pk = util.encodeBase64(kp.publicKey);
  localStorage.setItem(SK_KEY, util.encodeBase64(kp.secretKey));
  localStorage.setItem(PK_KEY, pk);
  return { secretKey: kp.secretKey, publicKeyB64: pk };
}

// Make sure the device has a key and the server has our latest public key.
export async function ensureE2EKey() {
  try {
    const { publicKeyB64 } = getKeyPair();
    if (localStorage.getItem(PUB_KEY) !== publicKeyB64) {
      await api.post('/users/public-key', { publicKey: publicKeyB64 });
      localStorage.setItem(PUB_KEY, publicKeyB64);
    }
  } catch { /* offline — will retry next launch */ }
}

const cache = new Map();
export async function fetchPublicKey(userId) {
  if (!userId) return '';
  if (cache.has(userId)) return cache.get(userId);
  try {
    const { data } = await api.get(`/users/${userId}/public-key`);
    cache.set(userId, data.publicKey || '');
    return data.publicKey || '';
  } catch { return ''; }
}

// Encrypt for a recipient's public key → "e2e:<nonce>:<cipher>" (both base64).
export function encryptFor(theirPublicKeyB64, plaintext) {
  const { secretKey } = getKeyPair();
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const box = nacl.box(util.decodeUTF8(plaintext), nonce, util.decodeBase64(theirPublicKeyB64), secretKey);
  return `e2e:${util.encodeBase64(nonce)}:${util.encodeBase64(box)}`;
}

// Decrypt a payload from a sender's public key. Returns null on failure.
export function decryptFrom(theirPublicKeyB64, payload) {
  try {
    if (!isEncrypted(payload) || !theirPublicKeyB64) return null;
    const [, nB64, cB64] = payload.split(':');
    const { secretKey } = getKeyPair();
    const opened = nacl.box.open(util.decodeBase64(cB64), util.decodeBase64(nB64), util.decodeBase64(theirPublicKeyB64), secretKey);
    return opened ? util.encodeUTF8(opened) : null;
  } catch { return null; }
}

export function isEncrypted(text) {
  return typeof text === 'string' && text.startsWith('e2e:');
}

// For previews/lists where we can't decrypt (no partner key on hand): never
// show raw ciphertext — show a neutral label instead.
export function previewText(text, label = 'Encrypted message') {
  return isEncrypted(text) ? label : text;
}

// ── Key backup / restore ──────────────────────────────────────────────────
// The secret key is the ONLY way to read your encrypted messages. Back it up
// so a new device (which would otherwise generate a fresh key) can restore it
// and keep your history.
export function exportSecretKey() {
  return localStorage.getItem(SK_KEY) || '';
}

export async function importSecretKey(skB64) {
  try {
    const sk = util.decodeBase64(String(skB64).trim());
    if (sk.length !== nacl.box.secretKeyLength) return false;
    const pk = nacl.box.keyPair.fromSecretKey(sk).publicKey;
    localStorage.setItem(SK_KEY, String(skB64).trim());
    localStorage.setItem(PK_KEY, util.encodeBase64(pk));
    localStorage.removeItem(PUB_KEY); // force republish of this (restored) key
    await ensureE2EKey();
    return true;
  } catch { return false; }
}
